import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import { FastlyImage } from '@components/common/image'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { GITHUB } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

export const GitHubSeo = formatSeoConfig({
  title: 'GitHub for Kids: Learn Collaboration & Coding',
  description:
    'GitHub classes for kids led by expert instructors. Save projects, track code and collaborate like a real developer at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/github-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-light-v2.jpg',
})

const BegineerGuide = () => (
  <div className="space-y-3">
    <h3 className="font-600 text-lg lg:text-xl">
      Why Should Kids Learn GitHub?
    </h3>
    <p>
      Learning GitHub teaches kids how to organize, save, and track their coding
      projects just like a super-organized notebook or a coding time machine.
    </p>
    <p>Here’s why it is important for kids to learn GitHub:</p>
    <ul>
      <li>
        Build Teamwork Skills: Kids can collaborate with friends on apps, games,
        or websites without losing progress.
      </li>
      <li>
        Learn Version Control: They understand how to manage changes, go back if
        something breaks, and keep projects neat.
      </li>
      <li>
        Develop Problem-Solving Habits: GitHub encourages logical thinking and
        planning for coding projects.
      </li>
      <li>
        Gain Real-World Skills: Kids learn the same tools and habits
        professional developers use every day.
      </li>
      <li>
        Boost Confidence: With GitHub, children can safely experiment, fix
        mistakes, and see their progress over time.
      </li>
    </ul>
    <p>
      In short, GitHub helps kids code smarter, work together, and take
      ownership of their projects—all while having fun creating games, apps, and
      websites!
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      How Do You Explain GitHub to a Child?
    </h3>
    <p>
      GitHub is like a magic notebook for your coding projects. Every time you
      make a change, like adding a new level to a game or fixing a mistake it
      remembers everything.
    </p>
    <p>In GitHub You can:</p>
    <ul>
      <li>See what changed, when, and who changed it</li>
      <li>
        Go back in time if something breaks, like a super-powered undo button
      </li>
      <li>Work with friends on the same project without messing anything up</li>
      <li>Show off your projects safely to teachers, family, or classmates</li>
    </ul>
    <p>
      With GitHub, coding becomes organized, fun, and collaborative. It’s like
      learning how real programmers build games, apps, and websites, while
      keeping all your ideas safe and easy to manage!
    </p>
    <h3 className="font-600 text-lg lg:text-xl">What Does GitHub Stand For?</h3>
    <p>
      GitHub stands for “Global Information Tracker”. You can think of it like a
      super-smart notebook for coding projects. It keeps track of every change
      you make, remembers who made the change, and lets you go back in time if
      something goes wrong.
    </p>
    <p>
      With GitHub, kids learn to organize projects, collaborate with friends,
      and code like real developers, all while keeping their work safe and easy
      to manage.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      An Easy Way to Understand GitHub for kids
    </h3>
    <p>
      Here’s a simple analogy to help you understand GitHub. Imagine you are
      writing a superhero comic book. Every time you finish a page, you make a
      copy and keep it safe. If you make a mistake later or want to change the
      story, you can always go back to the earlier version without losing your
      work. That safe copy is your GitHub version.
    </p>
    <p>
      GitHub is like a time machine for your projects, it keeps track of every
      change you make. It helps kids stay organized, fix mistakes easily, and
      even work together on the same project without mixing things up.
    </p>
    <p>
      This is exactly how kids learn GitHub in our kids GitHub classes; turning
      their coding projects into stories they can save, improve, and share!
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      What Can Kids Learn with GitHub?
    </h3>
    <p>Kids can learn a lot with GitHub, including:</p>

    <ul>
      <li>
        <strong>Version control:</strong> How to save and track every change in
        their code
      </li>
      <li>
        <strong>Problem-solving:</strong> Fixing mistakes by going back to
        earlier versions
      </li>
      <li>
        <strong>Organization skills:</strong> Keeping projects neat and
        structured
      </li>
      <li>
        <strong>Collaboration:</strong> Working on the same project with friends
        without overwriting each other’s work
      </li>
      <li>
        <strong>Real-world coding habits:</strong> Using tools that professional
        developers rely on every day
      </li>
    </ul>
    <p>
      With GitHub, kids don’t just code—they learn how to think, create, and
      collaborate like real programmers.
    </p>
    <div>
      <FastlyImage
        src={`/images/courses/git/git-2.jpg`}
        alt="What Can Kids Learn with GIT?"
        width={512}
        height={341}
      />
    </div>
  </div>
)

const ReactjsPage = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...GitHubSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[GITHUB].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Learn React.js with Top AI & Coding Course Instructors for Kids and Teens"
        description="Step into the fascinating world of React.js, where coding becomes a canvas for imagination and innovation! With Codingal’s interactive curriculum, students bring their ideas to life through animations, visuals, and interactive web projects  all created using simple lines of HTML, CSS, and React.js. In every session, learners use React.js to make web pages move, respond, and react,  building creative designs, interactive stories, and dynamic effects. As they experiment with loops, functions, events, and logic, kids learn the fundamentals of programming in a fun, visual, and hands-on way. Each project introduces essential coding concepts such as variables, conditional statements, functions, and event handling, while encouraging creativity and problem-solving. From creating a Fireworks Animation or an Interactive Quiz to building a Mini Game, students learn how React.js powers the interactive web experiences they see every day. As students progress, they transition from visual coding platforms like Scratch and Play Lab to text-based environments such as Replit, p5.js, or CodePen. They learn how HTML and CSS work together with React.js to design and style complete web pages,  preparing them for real-world web development. Perfect for kids aged 8–14, Codingal’s React.js lessons build a strong foundation in programming, boost creative confidence, and make learning code as exciting as playing a game!"
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={GITHUB}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={GITHUB} />
          <CourseFeatures
            courseTitle={GITHUB}
            title={<Trans>Engaging Website Development classes for kids</Trans>}
          />
          <ClassCurriculum
            courseName={GITHUB}
            title="Our GitHub Class Curriculum"
            description={
              <>
                The GitHub curriculum for kids is designed to take students from
                foundational concepts to practical coding collaboration skills.
                Beginning with setting up GitHub and learning basic commands,
                students gradually explore how to save versions of projects,
                undo mistakes, and collaborate on shared code. As they advance,
                kids learn branching, merging, and using GitHubHub to publish
                and share projects. Each lesson builds on the previous one,
                enabling students to manage coding projects confidently, work in
                teams, and apply version control in hands-on, real-world
                projects. It&apos;s like giving them a time machine for coding
                LEGOs!
              </>
            }
          />
          <Activities courseName={GITHUB} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn GitHub from experienced teachers. Our hand-picked AI &
                coding tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach. They’re truly
                  amazing, and it shows in our exceptional Net Promoter Score
                  (NPS) of 86! Students and parents consistently rate our
                  teachers 4.9 out of 5, and our curriculum earns a fantastic
                  4.8 out of 5. We’re so proud to have a faculty that’s 91%
                  female and 9% male, creating an inclusive, encouraging
                  learning environment where everyone feels welcome. Each tutor
                  is trained to understand individual student needs and deliver
                  personalized support, even in group settings. Plus, students
                  have 24/7 access to expert tutors for instant doubt
                  resolution, anytime they need help. This makes learning GitHub
                  a truly supported experience, especially through our virtual
                  GitHub lessons that connect kids with top educators worldwide.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Website Development
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  Codingal offers a nurturing environment where young learners
                  can explore the world of coding without fear. Our structured
                  curriculum, combined with supportive instructors, ensures that
                  students build a strong foundation in GitHub, preparing them
                  for exciting future endeavors in AI & coding.
                </span>
              </Trans>
            }
            courseName={GITHUB}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters GitHub through a STEM.org -
                  accredited program built on proven educational frameworks like
                  Bloom’s Taxonomy, BIDE, and STEAM. Our curriculum is aligned
                  with Common Core, state standards, AP Computer Science goals,
                  and the K - 12 Computer Science Framework - ensuring learning
                  that’s both age-appropriate and college-ready. Through
                  hands-on, project-based learning, students create real-world
                  apps like temperature converters and scientific calculators,
                  applying math, science, and engineering logic to solve
                  everyday problems. Backed by STEM.org’s rigorous audits, every
                  GitHub lesson lays a strong, future-proof foundation in STEM.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your GitHub certifications"
            description={
              <>
                In the Website Development track, students in grades 6–12 begin
                their journey with GitHub for Kids, a platform that teaches them
                how to store, manage, and track coding projects safely. While
                learning GitHub, students also start building web pages using
                HTML, including elements such as headings, paragraphs, links,
                images, lists, tables, and forms.
                <br />
                They learn to use semantic HTML to make their web pages
                accessible, organized, and professional-looking, similar to
                real-world websites. Through hands-on lessons and creative
                projects, students transform their code into fully functional
                web pages that they can test, share, and improve
                collaboratively.
                <br />
                By the end of the module, students not only understand how
                websites are built but also gain a solid foundation in version
                control, web design, and teamwork. This prepares them for more
                advanced skills, such as adding interactivity with JavaScript
                and exploring complex web technologies in the future.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our GitHub Classes Are Appropriate for Middle School & High School Students"
            description={
              <>
                At Codingal, our foundational GitHub curriculum introduces
                middle and high school students to one of the most important
                tools used by developers worldwide: version control.
                <br />
                Students begin with simple, beginner-friendly concepts such as
                repositories, commits, and saving changes, learning how to
                organize and track their coding projects effectively. Through
                hands-on activities, they see how each change is recorded and
                how to manage their code safely.
                <br />
                As they advance, students explore more complex topics like
                branches, merges, and collaborative workflows on GitHub. This
                ensures a clear, steady learning curve while teaching them how
                real developers work together without losing progress.
                <br />
                With every project, learners gain experience in working like
                professional developers, saving multiple versions of their code,
                and confidently collaborating with teammates.
                <br />
                By the end of the course, students not only understand GitHub as
                a version control system but also develop the skills and
                confidence to manage coding projects, collaborate effectively in
                teams, and prepare for advanced areas like AI and software
                development.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'cgm']} />
          <ContentCard
            urlQuery={urlQuery}
            title={<>The Ultimate Guide to Github for Kids</>}
            description={<BegineerGuide />}
          />
          <Faq
            courseName={GITHUB}
            title="Frequently Asked Questions: GitHub for Kids"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={GITHUB}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Github for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default ReactjsPage
